import { useEffect, useState } from "react";
import type { SyntheticEvent } from "react";
import { teammatesApi } from "../api";
import { notifyTeammatesUpdated } from "./useTeammates";
import type { DatasourceOption } from "../types/chat";
import type { TeamOption, Teammate, TeammateAppearance, TeammateInput } from "../types/teammates";

export function useTeammateForm(
  teammate: Teammate | null | undefined,
  datasourceOptions: DatasourceOption[],
  onSaved: (teammate: Teammate) => void,
) {
  const [name, setName] = useState(teammate?.name ?? "");
  const [teamId, setTeamId] = useState(teammate?.team_id ?? "");
  const [description, setDescription] = useState(teammate?.description ?? "");
  const [prompt, setPrompt] = useState(teammate?.prompt ?? "");
  const [allSources, setAllSources] = useState(!teammate?.datasource_keys);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(teammate?.datasource_keys ?? []);
  const [appearance, setAppearance] = useState<TeammateAppearance>(teammate?.appearance ?? {});
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    teammatesApi.listTeams()
      .then((res) => setTeams(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeams([]));
  }, []);

  const unknownKeys = selectedKeys.filter((key) => !datasourceOptions.some((o) => o.key === key));
  const canSave = name.trim().length > 0 && (allSources || selectedKeys.length > 0) && !saving;
  const teamName =
    teams.find((team) => team.id === teamId)?.name ??
    (teamId && teamId === teammate?.team_id ? teammate.team_name : null);

  function toggleKey(key: string) {
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave) return;
    const payload: TeammateInput = {
      name: name.trim(),
      description: description.trim() || null,
      prompt,
      datasource_keys: allSources ? null : selectedKeys,
      team_id: teamId || null,
      appearance: Object.values(appearance).some((value) => value != null) ? appearance : null,
    };
    setSaving(true);
    const request = teammate ? teammatesApi.update(teammate.id, payload) : teammatesApi.create(payload);
    request
      .then((res) => {
        notifyTeammatesUpdated();
        onSaved(res.data);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  }

  return {
    name,
    setName,
    teamId,
    setTeamId,
    teamName,
    description,
    setDescription,
    prompt,
    setPrompt,
    allSources,
    setAllSources,
    selectedKeys,
    toggleKey,
    appearance,
    setAppearance,
    unknownKeys,
    teams,
    saving,
    canSave,
    submit,
  };
}

export type TeammateForm = ReturnType<typeof useTeammateForm>;
